import React, { useMemo } from 'react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import {
  DispatchDetail,
  DispatchDetailBatch,
  DispatchRiderSnapshot,
  UnscannedItem,
  UnscannedItemsError,
} from '@/services/api/dispatches';

interface RiderGroupSummary {
  rider: DispatchRiderSnapshot;
  batches: DispatchDetailBatch[];
  totalItems: number;
  totalWeightKg: number;
  assignedTimeMinutes: number;
}

interface CommitDispatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  dispatch: DispatchDetail;
  plannedRiderGroups: RiderGroupSummary[];
  submitting: boolean;
  onSubmit: () => Promise<void>;
  /**
   * Set when the last commit attempt was rejected by the shipment-complete
   * gate (409 UNSCANNED_ITEMS, §D10). Rendered as an item list grouped by
   * AWB so ops knows exactly which barang to scan or mark hilang.
   */
  unscannedError?: UnscannedItemsError | null;
}

const fmtMinutes = (min: number) => {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

const CommitDispatchModal: React.FC<CommitDispatchModalProps> = ({
  isOpen,
  onClose,
  dispatch,
  plannedRiderGroups,
  submitting,
  onSubmit,
  unscannedError,
}) => {
  const plannedBatches = plannedRiderGroups.reduce(
    (n, g) => n + g.batches.length,
    0
  );
  const plannedItems = plannedRiderGroups.reduce(
    (n, g) => n + g.totalItems,
    0
  );
  const noPlanned = plannedBatches === 0;
  // Only items that will actually ship gate the commit — deferred items sit in
  // the rider-less DEFERRED batch and are excluded from this run, so counting
  // them here produced nonsense like "17 of 7 items belum di-scan" (§D9/§D10).
  const unscannedCount = dispatch.activeUnscannedItemCount ?? 0;
  const deferredUnscannedCount = dispatch.deferredUnscannedItemCount ?? 0;

  // Derived from the batches tree — dispatch.deferredItemCount can be stale
  // after a BE-side refresh/replan. Defer is stop-scoped now: everything held
  // back lives in the rider-less DEFERRED batch(es), no per-item flag.
  const deferredItemCount = useMemo(() => {
    let n = 0;
    for (const b of dispatch.batches) {
      if (b.status === 'DEFERRED') n += b.itemCount;
    }
    return n;
  }, [dispatch]);

  // Group the gate's rejected items by AWB — an AWB ships atomically, so
  // ops thinks in shipments ("AWB X is missing 2 items"), not loose items.
  const unscannedByAwb = useMemo(() => {
    if (!unscannedError) return [];
    const groups = new Map<string, UnscannedItem[]>();
    for (const item of unscannedError.unscannedItems) {
      const key = item.bookingId || item.shipmentId;
      const list = groups.get(key) ?? [];
      list.push(item);
      groups.set(key, list);
    }
    return Array.from(groups.entries());
  }, [unscannedError]);

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-check-double-line" />}
      iconTone="primary"
      title="Commit dispatch"
      subtitle={`Lock in ${dispatch.code} and assign items to riders.`}
      size="lg"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-end w-full gap-2">
          <Button tone="neutral" style="stroke" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={submitting || noPlanned}>
            {submitting ? 'Committing…' : 'Commit dispatch'}
          </Button>
        </div>
      }
    >
      {noPlanned ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-[13px] text-amber-800 font-semibold">
            No planned batches to commit.
          </p>
          <p className="mt-1 text-[12px] text-amber-700">
            Reactivate at least one batch from Holding (assigning a rider)
            before committing.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Commit-gate rejection (§D10) — the last attempt bounced. */}
          {unscannedError && (
            <div className="rounded-lg border border-red-200 bg-red-50">
              <div className="border-b border-red-200 px-4 py-3">
                <p className="text-[13px] font-semibold text-red-800">
                  <i className="ri-error-warning-line mr-1 text-[14px] align-[-1px]" />
                  Commit ditolak — {unscannedError.unscannedItemCount} item
                  belum di-scan
                </p>
                <p className="mt-1 text-[12px] text-red-700 leading-relaxed">
                  {unscannedError.message}
                </p>
              </div>
              <ul className="divide-y divide-red-100 max-h-56 overflow-y-auto">
                {unscannedByAwb.map(([awb, items]) => (
                  <li key={awb} className="px-4 py-2.5">
                    <p className="font-mono text-[12px] font-semibold text-text-strong-950 tabular-nums">
                      {awb}
                    </p>
                    <ul className="mt-1 space-y-0.5">
                      {items.map((it) => (
                        <li
                          key={it.itemId}
                          className="flex items-center gap-2 text-[11px] text-text-sub-600"
                        >
                          <span className="font-medium text-text-strong-950">
                            {it.itemName}
                          </span>
                          {it.invoice && (
                            <span className="font-mono tabular-nums">
                              {it.invoice}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
              <div className="border-t border-red-200 px-4 py-2.5">
                <p className="text-[11px] text-red-700 leading-relaxed">
                  Sudah datang → scan item di halaman Inbound. Barang hilang →
                  remove stop items dengan "Mark hilang" di batch, lalu commit
                  ulang. Defer saja tidak cukup untuk item yang belum di-scan.
                </p>
              </div>
            </div>
          )}

          {/* Pre-commit heads-up (§D9) — commit will bounce until these are
              scanned or removed, so warn before ops even tries. */}
          {!unscannedError && unscannedCount > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-[13px] text-amber-800 font-semibold">
                <i className="ri-alert-line mr-1 text-[14px] align-[-1px]" />
                {unscannedCount} of {plannedItems} items yang akan dikirim belum
                di-scan di hub.
              </p>
              <p className="mt-1 text-[12px] text-amber-700 leading-relaxed">
                Commit will be rejected while any shipment in this dispatch
                still has an unscanned item. Scan them at Inbound, or remove
                missing goods via "Mark hilang" on the stop.
                {deferredUnscannedCount > 0 && (
                  <>
                    {' '}
                    <span className="text-amber-800/80">
                      ({deferredUnscannedCount} item deferred juga belum di-scan
                      — tidak ikut terkirim di dispatch ini.)
                    </span>
                  </>
                )}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <SummaryStat
              label="Planned batches"
              value={String(plannedBatches)}
            />
            <SummaryStat label="Items shipping" value={String(plannedItems)} />
            <SummaryStat
              label="Deferred items"
              value={String(deferredItemCount)}
              hint={deferredItemCount > 0 ? "Won't ship this run" : undefined}
            />
            <SummaryStat
              label="Unbatchable"
              value={String(dispatch.unbatchableCount)}
              hint={
                dispatch.unbatchableCount > 0 ? 'Handle offline' : undefined
              }
            />
          </div>

          <div className="rounded-lg border border-stroke-soft-200 bg-bg-weak-50">
            <div className="border-b border-stroke-soft-200 px-3 py-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
                Per-rider workload
              </p>
            </div>
            <ul className="divide-y divide-stroke-soft-200">
              {plannedRiderGroups.map((g) => {
                const pct =
                  dispatch.shiftMinutes > 0
                    ? g.assignedTimeMinutes / dispatch.shiftMinutes
                    : 0;
                const tight = pct >= 0.9;
                return (
                  <li
                    key={g.rider.id}
                    className="flex items-center justify-between gap-3 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-text-strong-950 truncate">
                        {g.rider.name}
                      </p>
                      <p className="font-mono text-[11px] text-text-sub-600 tabular-nums">
                        {g.rider.code}
                      </p>
                    </div>
                    <div className="text-right text-[11px] tabular-nums text-text-sub-600">
                      <p>
                        <strong className="text-text-strong-950">
                          {g.batches.length}
                        </strong>{' '}
                        trip{g.batches.length === 1 ? '' : 's'} ·{' '}
                        <strong className="text-text-strong-950">
                          {g.totalItems}
                        </strong>{' '}
                        items
                      </p>
                      <p
                        className={
                          tight
                            ? 'text-amber-700 font-semibold'
                            : 'text-text-sub-600'
                        }
                      >
                        {fmtMinutes(g.assignedTimeMinutes)} /{' '}
                        {fmtMinutes(dispatch.shiftMinutes)} (
                        {Math.round(pct * 100)}%)
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

        </div>
      )}
    </FormModal>
  );
};

const SummaryStat: React.FC<{
  label: string;
  value: string;
  hint?: string;
}> = ({ label, value, hint }) => (
  <div className="rounded-lg border border-stroke-soft-200 bg-white px-3 py-2">
    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-text-soft-400">
      {label}
    </p>
    <p className="mt-0.5 text-[18px] font-extrabold tracking-tight text-text-strong-950 tabular-nums">
      {value}
    </p>
    {hint && <p className="text-[11px] text-text-sub-600 mt-0.5">{hint}</p>}
  </div>
);

export default CommitDispatchModal;
